import React, { useState, useEffect } from 'react';
import { userApi, inviteApi, projectApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Table, Badge, Spinner } from '../components/UI';
import { Plus, PencilSimple, Trash, UserCircle, Lock, LockOpen, Key, EnvelopeSimple, Copy, ArrowClockwise, X as XIcon } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const ROLES = ['Admin', 'General Manager', 'Finance Manager', 'Project Manager', 'Engineer', 'Supervisor', 'Store Keeper', 'Purchaser', 'Accountant', 'Coordinator', 'Team Leader'];
const APP_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'Engineer', phone: '', reports_to: ''
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('users');
  const [invites, setInvites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'Engineer', project_access: [], message: '' });
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => { loadUsers(); loadInvites(); loadProjects(); }, []);

  const loadUsers = async () => {
    try {
      const response = await userApi.list();
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadInvites = async () => {
    try { const res = await inviteApi.list(); setInvites(res.data); }
    catch { /* ignore for non-admins */ }
  };

  const loadProjects = async () => {
    try { const res = await projectApi.list(); setProjects(res.data); }
    catch { /* ignore */ }
  };

  const isAdmin = ['Admin', 'IT Admin', 'Super Admin'].includes(currentUser?.role);

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', role: 'Engineer', phone: '', reports_to: '' });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({ 
      name: user.name, email: user.email, password: '', role: user.role, 
      phone: user.phone || '', reports_to: user.reports_to || ''
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editingUser) {
        const updateData = { ...formData };
        delete updateData.email;
        delete updateData.password;
        await userApi.update(editingUser.id, updateData);
      } else {
        if (!formData.password) { setError('Temporary password is required'); setSaving(false); return; }
        await userApi.create(formData);
      }
      setShowModal(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleBlock = async (userId) => {
    if (!window.confirm('Block this user? They will not be able to log in.')) return;
    try { await userApi.blockUser(userId); loadUsers(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleUnblock = async (userId) => {
    if (!window.confirm('Unblock this user?')) return;
    try { await userApi.unblockUser(userId); loadUsers(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const openResetModal = (userId) => {
    setResetUserId(userId);
    setResetPassword('');
    setShowResetModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPassword.length < 6) { alert('Password must be at least 6 characters'); return; }
    try {
      await userApi.resetPassword(resetUserId, { new_password: resetPassword });
      setShowResetModal(false);
      alert('Password reset. User will be prompted to change it on next login.');
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    try { await userApi.delete(userId); loadUsers(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to deactivate'); }
  };

  const handleInvite = async (e) => {
    e.preventDefault(); setError('');
    try {
      await inviteApi.create(inviteForm);
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'Engineer', project_access: [], message: '' });
      loadInvites();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to create invite'); }
  };

  const handleRevokeInvite = async (id) => {
    if (!window.confirm('Revoke this invite?')) return;
    try { await inviteApi.revoke(id); loadInvites(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleResendInvite = async (id) => {
    try { const res = await inviteApi.resend(id); loadInvites(); alert('Invite link regenerated with new expiry.'); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const getInviteLink = (invite) => {
    const base = APP_URL.replace('/api', '').replace(':8001', ':3000');
    return `${base}/invite/${invite.token}`;
  };

  const copyLink = (invite) => {
    navigator.clipboard.writeText(getInviteLink(invite));
    setCopiedId(invite.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const pendingInvites = invites.filter(i => i.status === 'pending');
  const pastInvites = invites.filter(i => i.status !== 'pending');

  const columns = [
    { 
      header: 'User', 
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${row.is_active ? 'bg-[#002FA7]' : 'bg-[#999]'}`}>
            <UserCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-[#0A0A0A]">{row.name}</p>
            <p className="text-xs text-[#666666]">{row.email}</p>
          </div>
        </div>
      )
    },
    { header: 'Role', render: (row) => <Badge variant="primary">{row.role}</Badge> },
    { header: 'Phone', render: (row) => <span className="text-[#666]">{row.phone || '-'}</span> },
    { 
      header: 'Status', 
      render: (row) => (
        <div className="flex items-center gap-2">
          <Badge variant={row.is_active ? 'success' : 'danger'}>
            {row.is_active ? 'Active' : 'Blocked'}
          </Badge>
          {row.must_change_password && <Badge variant="warning">Must Change PW</Badge>}
        </div>
      )
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-1">
          <button onClick={() => openEditModal(row)} className="p-1.5 text-[#666] hover:text-[#002FA7] hover:bg-[#002FA7]/5 rounded" title="Edit" data-testid={`edit-user-${row.id}`}>
            <PencilSimple className="w-4 h-4" />
          </button>
          {isAdmin && row.id !== currentUser.id && (
            <>
              <button onClick={() => openResetModal(row.id)} className="p-1.5 text-[#666] hover:text-[#FFAB00] hover:bg-[#FFAB00]/10 rounded" title="Reset Password" data-testid={`reset-pw-${row.id}`}>
                <Key className="w-4 h-4" />
              </button>
              {row.is_active ? (
                <button onClick={() => handleBlock(row.id)} className="p-1.5 text-[#666] hover:text-[#FF3333] hover:bg-[#FF3333]/10 rounded" title="Block User" data-testid={`block-user-${row.id}`}>
                  <Lock className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={() => handleUnblock(row.id)} className="p-1.5 text-[#666] hover:text-[#00C853] hover:bg-[#00C853]/10 rounded" title="Unblock User" data-testid={`unblock-user-${row.id}`}>
                  <LockOpen className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => handleDelete(row.id)} className="p-1.5 text-[#666] hover:text-[#FF3333] hover:bg-[#FF3333]/10 rounded" title="Deactivate" data-testid={`del-user-${row.id}`}>
                <Trash className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      )
    },
  ];

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">User Management</h1>
          <p className="text-[#666666]">Manage users, roles, invitations, and access</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="secondary" onClick={() => { setError(''); setInviteForm({ email: '', role: 'Engineer', project_access: [], message: '' }); setShowInviteModal(true); }} data-testid="invite-user-btn">
              <EnvelopeSimple className="w-4 h-4 mr-2" /> Invite User
            </Button>
          )}
          <Button onClick={openCreateModal} data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-2" /> Add User
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E0E0E0]" data-testid="user-tabs">
        <button onClick={() => setTab('users')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'users' ? 'border-[#1A1F36] text-[#1A1F36]' : 'border-transparent text-[#888] hover:text-[#555]'}`}
          data-testid="tab-users">Users ({users.length})</button>
        {isAdmin && (
          <button onClick={() => setTab('invites')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === 'invites' ? 'border-[#1A1F36] text-[#1A1F36]' : 'border-transparent text-[#888] hover:text-[#555]'}`}
            data-testid="tab-invites">
            Invitations {pendingInvites.length > 0 && <span className="bg-[#002FA7] text-white text-[10px] px-1.5 py-0.5 rounded-full">{pendingInvites.length}</span>}
          </button>
        )}
      </div>

      {/* Users Tab */}
      {tab === 'users' && (
        <Card><Table columns={columns} data={users} /></Card>
      )}

      {/* Invites Tab */}
      {tab === 'invites' && (
        <div className="space-y-4">
          {pendingInvites.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-2">Pending Invitations</h3>
              <Card className="divide-y divide-[#F0F0F0]">
                {pendingInvites.map(inv => (
                  <div key={inv.id} className="p-3 hover:bg-[#FAFAFA]" data-testid={`invite-row-${inv.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-[#002FA7]/10 rounded-lg flex items-center justify-center">
                          <EnvelopeSimple className="w-4 h-4 text-[#002FA7]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-[#0A0A0A]">{inv.email}</p>
                          <div className="flex gap-2 text-xs text-[#666] mt-0.5">
                            <Badge variant="primary">{inv.role}</Badge>
                            <span>by {inv.invited_by_name}</span>
                            <span>expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => copyLink(inv)} className="px-2 py-1 text-xs rounded flex items-center gap-1 transition-colors hover:bg-[#002FA7]/5 text-[#002FA7]" data-testid={`copy-link-${inv.id}`}>
                          <Copy className="w-3.5 h-3.5" /> {copiedId === inv.id ? 'Copied!' : 'Copy Link'}
                        </button>
                        <button onClick={() => handleResendInvite(inv.id)} className="p-1.5 text-[#666] hover:text-[#002FA7] hover:bg-[#002FA7]/5 rounded" title="Regenerate Link" data-testid={`resend-${inv.id}`}>
                          <ArrowClockwise className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleRevokeInvite(inv.id)} className="p-1.5 text-[#666] hover:text-[#FF3333] hover:bg-[#FF3333]/10 rounded" title="Revoke" data-testid={`revoke-${inv.id}`}>
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {/* Shareable link */}
                    <div className="mt-2 p-2 rounded text-xs font-mono break-all" style={{ backgroundColor: 'var(--muted)', color: 'var(--text-secondary)' }}>
                      {getInviteLink(inv)}
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          )}

          {pastInvites.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-2">Past Invitations</h3>
              <Card className="divide-y divide-[#F0F0F0]">
                {pastInvites.map(inv => (
                  <div key={inv.id} className="p-3 flex items-center justify-between opacity-60">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-[#E5E5E5] rounded-lg flex items-center justify-center">
                        <EnvelopeSimple className="w-4 h-4 text-[#999]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#0A0A0A]">{inv.email}</p>
                        <div className="flex gap-2 text-xs text-[#666]">
                          <span>{inv.role}</span>
                          <Badge variant={inv.status === 'accepted' ? 'success' : inv.status === 'revoked' ? 'danger' : 'default'}>{inv.status}</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          )}

          {invites.length === 0 && (
            <Card className="p-8 text-center text-[#666]">
              <EnvelopeSimple className="w-10 h-10 mx-auto mb-3 text-[#E0E0E0]" />
              No invitations sent yet. Click "Invite User" to get started.
            </Card>
          )}
        </div>
      )}

      {/* Create / Edit User Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingUser ? 'Edit User' : 'Add User'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="p-3 bg-[#FF3333]/10 text-[#FF3333] rounded-lg text-sm">{error}</div>}
          
          <Input label="Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="user-name-input" />
          
          {!editingUser && (
            <>
              <Input label="Email (User ID)" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="user-email-input" />
              <Input label="Temporary Password" type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required data-testid="user-password-input" />
              <p className="text-xs text-[#888]">User will be required to change this password on first login.</p>
            </>
          )}
          
          <Select label="Role" value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} data-testid="user-role-select">
            {ROLES.map(role => <option key={role} value={role}>{role}</option>)}
          </Select>
          
          <Input label="Phone" type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
          
          <Select label="Reports To" value={formData.reports_to} onChange={(e) => setFormData({...formData, reports_to: e.target.value})}>
            <option value="">-- Select Manager --</option>
            {users.filter(u => u.id !== editingUser?.id).map(u => (
              <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
            ))}
          </Select>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-user-btn">{saving ? 'Saving...' : editingUser ? 'Update' : 'Create User'}</Button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={showResetModal} onClose={() => setShowResetModal(false)} title="Reset User Password">
        <form onSubmit={handleResetPassword} className="space-y-4">
          <p className="text-sm text-[#666]">Set a new temporary password. The user will be required to change it on their next login.</p>
          <Input label="New Temporary Password" type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} required placeholder="At least 6 characters" data-testid="reset-pw-input" />
          <div className="flex justify-end space-x-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowResetModal(false)}>Cancel</Button>
            <Button type="submit" data-testid="reset-pw-submit">Reset Password</Button>
          </div>
        </form>
      </Modal>

      {/* Invite User Modal */}
      <Modal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} title="Invite User">
        <form onSubmit={handleInvite} className="space-y-4" data-testid="invite-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <div>
            <Input label="Email Address" type="email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} required placeholder="user@company.com" data-testid="invite-email-input" />
            <p className="text-xs text-[#888] mt-1">An invite link will be generated for this email</p>
          </div>
          <Select label="Role" value={inviteForm.role} onChange={e => setInviteForm({...inviteForm, role: e.target.value})} data-testid="invite-role-select">
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </Select>
          <div>
            <label className="block text-xs font-medium mb-1 text-[#555]">Project Access (optional)</label>
            <div className="max-h-32 overflow-y-auto border border-[#E0E0E0] rounded-lg p-2 space-y-1">
              {projects.map(p => (
                <label key={p.id} className="flex items-center gap-2 text-sm text-[#555] cursor-pointer hover:bg-[#FAFAFA] px-1 rounded">
                  <input type="checkbox" checked={inviteForm.project_access.includes(p.id)}
                    onChange={e => {
                      const acc = e.target.checked ? [...inviteForm.project_access, p.id] : inviteForm.project_access.filter(id => id !== p.id);
                      setInviteForm({...inviteForm, project_access: acc});
                    }} className="accent-[#002FA7]" />
                  {p.name}
                </label>
              ))}
              {projects.length === 0 && <p className="text-xs text-[#999]">No projects available</p>}
            </div>
          </div>
          <Input label="Personal Message (optional)" value={inviteForm.message} onChange={e => setInviteForm({...inviteForm, message: e.target.value})} placeholder="Welcome to the team!" />
          <div className="flex justify-end space-x-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowInviteModal(false)}>Cancel</Button>
            <Button type="submit" data-testid="invite-submit-btn"><EnvelopeSimple className="w-4 h-4 mr-1" /> Send Invite</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
